import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Cpu,
  FileText,
  HardDrive,
  LayoutDashboard,
  Network,
  RefreshCw,
  Server,
  TrendingUp,
  Users,
  XCircle,
  Eye,
} from 'lucide-react';
import { ViewTabs } from './ui';
import ErrorLogsTab from './monitoring/ErrorLogsTab';
import AppLogsTab from './monitoring/AppLogsTab';
import SystemDiagnosticsTab from './monitoring/SystemDiagnosticsTab';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const formatTimestampUTC5 = (timestamp) => {
  try {
    return new Date(timestamp).toLocaleString('en-US', {
      timeZone: 'Asia/Karachi',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return timestamp;
  }
};

const SectionLoader = ({ height = 'h-32' }) => (
  <div
    className={`bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6 flex items-center justify-center ${height}`}
  >
    <div className="flex items-center gap-3">
      <RefreshCw className="animate-spin h-5 w-5 text-primary-500" />
      <span className="text-sm text-secondary-500">Loading...</span>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showActiveUsersModal, setShowActiveUsersModal] = useState(false);
  const [activeUsersDetails, setActiveUsersDetails] = useState([]);
  const [loadingActiveUsers, setLoadingActiveUsers] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [errorHourCount, setErrorHourCount] = useState(null);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/monitoring/dashboard', {
        headers: authHeaders(),
      });
      setMetrics(response.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchErrorBadge = async () => {
    try {
      const res = await axios.get('/api/monitoring/error-logs/stats', {
        headers: authHeaders(),
      });
      setErrorHourCount(res.data?.last_hour ?? 0);
    } catch {
      /* ignore */
    }
  };

  const handleFailedRequestsClick = () => {
    setActiveTab('errors');
  };

  const fetchActiveUsersDetails = async () => {
    try {
      setLoadingActiveUsers(true);
      const response = await axios.get('/api/monitoring/active-users-details', {
        headers: authHeaders(),
      });
      setActiveUsersDetails(response.data.active_users || []);
    } catch (err) {
      console.error('Failed to fetch active users details:', err);
      setActiveUsersDetails([]);
    } finally {
      setLoadingActiveUsers(false);
    }
  };

  const handleActiveUsersClick = async () => {
    setShowActiveUsersModal(true);
    await fetchActiveUsersDetails();
  };

  useEffect(() => {
    fetchMetrics();
    fetchErrorBadge();
    const interval = setInterval(() => {
      fetchMetrics();
      fetchErrorBadge();
    }, 300000);
    return () => clearInterval(interval);
  }, []);

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-green-700 bg-green-50 dark:bg-green-900/20';
      case 'unhealthy':
        return 'text-red-700 bg-red-50 dark:bg-red-900/20';
      case 'degraded':
        return 'text-amber-700 bg-amber-50 dark:bg-amber-900/20';
      default:
        return 'text-secondary-600 bg-secondary-100 dark:bg-secondary-700';
    }
  };

  const getMetricCard = (title, value, icon, color = 'blue', subtitle = '', onClick = null) => (
    <div
      className={`bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 border-l-4 p-4 sm:p-6 ${
        onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
      }`}
      style={{ borderLeftColor: color === 'red' ? '#ef4444' : color === 'green' ? '#22c55e' : color === 'purple' ? '#a855f7' : color === 'orange' ? '#f97316' : '#2563eb' }}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onClick();
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium text-secondary-500 truncate">{title}</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-secondary-900 dark:text-white truncate">
            {value}
          </p>
          {subtitle && (
            <p className="text-xs sm:text-sm text-secondary-500 truncate">{subtitle}</p>
          )}
        </div>
        <div className="p-2 sm:p-3 rounded-full bg-secondary-50 dark:bg-secondary-700 flex-shrink-0 ml-2">
          {icon}
        </div>
      </div>
    </div>
  );

  if (error && !loading && activeTab === 'overview' && !metrics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">Failed to load dashboard: {error}</p>
          <button onClick={fetchMetrics} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'System Overview', icon: LayoutDashboard },
    {
      id: 'errors',
      label:
        errorHourCount != null && errorHourCount > 0
          ? `Error Logs (${errorHourCount}/hr)`
          : 'Error Logs',
      icon: AlertTriangle,
    },
    { id: 'app-logs', label: 'Application Logs', icon: FileText },
    { id: 'diagnostics', label: 'System Diagnostics', icon: Server },
  ];

  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 p-4 sm:p-6 overflow-x-hidden">
      <div className="w-full min-w-0 max-w-none overflow-hidden">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-white">
                Monitoring and Analytics
              </h1>
              <p className="text-secondary-500">
                System health, failed API requests, logs, and host diagnostics
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              {lastUpdated && (
                <p className="text-sm text-secondary-500">
                  Last updated: {formatTimestampUTC5(lastUpdated)}
                </p>
              )}
              <button
                onClick={() => {
                  fetchMetrics();
                  fetchErrorBadge();
                }}
                className="btn-primary inline-flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        <ViewTabs
          tabs={tabs}
          activeId={activeTab}
          onChange={setActiveTab}
          className="mb-6"
        />

        {activeTab === 'overview' && (
          <>
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4 flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                System Health
              </h2>
              {loading && !metrics ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <SectionLoader />
                  <SectionLoader />
                  <SectionLoader />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div
                    className={`rounded-lg border border-secondary-200 dark:border-secondary-700 p-4 sm:p-6 ${getHealthStatusColor(
                      metrics?.health?.status
                    )}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium">Overall Status</p>
                        <p className="text-lg sm:text-xl lg:text-2xl font-bold capitalize truncate">
                          {metrics?.health?.status || 'Unknown'}
                        </p>
                        <p className="text-xs sm:text-sm truncate">
                          Uptime:{' '}
                          {Math.floor((metrics?.health?.uptime_seconds || 0) / 3600)}h{' '}
                          {Math.floor(((metrics?.health?.uptime_seconds || 0) % 3600) / 60)}m
                        </p>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        {metrics?.health?.status === 'healthy' ? (
                          <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-green-500" />
                        ) : (
                          <XCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />
                        )}
                      </div>
                    </div>
                  </div>

                  {metrics?.health?.checks &&
                    Object.entries(metrics.health.checks).map(([service, check]) => (
                      <div
                        key={service}
                        className={`rounded-lg border border-secondary-200 dark:border-secondary-700 p-4 sm:p-6 ${getHealthStatusColor(
                          check.status
                        )}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium capitalize truncate">
                              {service.replace(/_/g, ' ')}
                            </p>
                            <p className="text-base sm:text-lg font-semibold capitalize truncate">
                              {check.status}
                            </p>
                            <p className="text-xs sm:text-sm truncate">
                              {(() => {
                                const ms =
                                  check.response_time_ms ??
                                  check.details?.response_time_ms ??
                                  check.response_time;
                                return ms != null ? `${ms}ms` : '—';
                              })()}
                            </p>
                          </div>
                          <div className="flex-shrink-0 ml-2">
                            {check.status === 'healthy' ? (
                              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4 flex items-center">
                <Server className="h-5 w-5 mr-2" />
                System Resources
              </h2>
              {loading && !metrics ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <SectionLoader />
                  <SectionLoader />
                  <SectionLoader />
                  <SectionLoader />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {getMetricCard(
                    'CPU Usage',
                    `${metrics?.system?.system?.cpu_percent || 0}%`,
                    <Cpu className="h-6 w-6 text-primary-500" />,
                    'blue'
                  )}
                  {getMetricCard(
                    'Memory Usage',
                    `${metrics?.system?.system?.memory_percent || 0}%`,
                    <HardDrive className="h-6 w-6 text-green-500" />,
                    'green',
                    `${metrics?.system?.system?.memory_available_gb || 0}GB available`
                  )}
                  {getMetricCard(
                    'Disk Usage',
                    `${metrics?.system?.system?.disk_percent || 0}%`,
                    <HardDrive className="h-6 w-6 text-purple-500" />,
                    'purple',
                    `${metrics?.system?.system?.disk_free_gb || 0}GB free`
                  )}
                  {getMetricCard(
                    'Network Connections',
                    metrics?.system?.network?.connections || 0,
                    <Network className="h-6 w-6 text-orange-500" />,
                    'orange'
                  )}
                </div>
              )}
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4 flex items-center">
                <TrendingUp className="h-5 w-5 mr-2" />
                Performance Metrics
              </h2>
              {loading && !metrics ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <SectionLoader />
                  <SectionLoader />
                  <SectionLoader />
                  <SectionLoader />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {getMetricCard(
                    'Total API Requests',
                    metrics?.business?.http_requests?.total ?? 0,
                    <Activity className="h-6 w-6 text-primary-500" />,
                    'blue'
                  )}
                  {getMetricCard(
                    'Avg Response Time',
                    `${Number(metrics?.business?.http_requests?.duration_avg || 0).toFixed(3)}s`,
                    <Clock className="h-6 w-6 text-green-500" />,
                    'green'
                  )}
                  {getMetricCard(
                    'Active Users',
                    metrics?.business?.users?.active || 0,
                    <Users className="h-6 w-6 text-purple-500" />,
                    'purple',
                    '',
                    handleActiveUsersClick
                  )}
                  {getMetricCard(
                    'Failed API Requests',
                    metrics?.business?.http_requests?.failed_total || 0,
                    <Eye className="h-6 w-6 text-red-500" />,
                    'red',
                    errorHourCount != null ? `${errorHourCount} in last hour — click for details` : 'Click for details',
                    handleFailedRequestsClick
                  )}
                </div>
              )}
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                System Information
              </h2>
              {loading && !metrics ? (
                <SectionLoader height="h-48" />
              ) : (
                <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <h3 className="text-base sm:text-lg font-medium text-secondary-900 dark:text-white mb-3">
                        Process Details
                      </h3>
                      <div className="space-y-2 text-xs sm:text-sm">
                        <div className="flex justify-between">
                          <span className="text-secondary-500">PID:</span>
                          <span className="font-medium">{metrics?.system?.process?.pid || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-secondary-500">Memory Usage:</span>
                          <span className="font-medium">
                            {metrics?.system?.process?.memory_mb || 0} MB
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-secondary-500">Threads:</span>
                          <span className="font-medium">
                            {metrics?.system?.process?.threads || 0}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-secondary-500">Open Files:</span>
                          <span className="font-medium">
                            {metrics?.system?.process?.open_files || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-medium text-secondary-900 dark:text-white mb-3">
                        System Details
                      </h3>
                      <div className="space-y-2 text-xs sm:text-sm">
                        <div className="flex justify-between">
                          <span className="text-secondary-500">Platform:</span>
                          <span className="font-medium">
                            {metrics?.system?.system?.platform || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-secondary-500">Python Version:</span>
                          <span className="font-medium">
                            {metrics?.system?.system?.python_version?.split(' ')[0] || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-secondary-500">CPU Cores:</span>
                          <span className="font-medium">
                            {metrics?.system?.system?.cpu_count || 0}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-secondary-500">Total Memory:</span>
                          <span className="font-medium">
                            {metrics?.system?.system?.memory_total_gb || 0} GB
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {metrics?.alerts && metrics.alerts.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Active Alerts
                </h2>
                <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4 space-y-2">
                  {metrics.alerts.map((alert, idx) => (
                    <div key={idx} className="text-sm text-amber-700 dark:text-amber-300">
                      {typeof alert === 'string' ? alert : alert.message || JSON.stringify(alert)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'errors' && <ErrorLogsTab />}
        {activeTab === 'app-logs' && <AppLogsTab />}
        {activeTab === 'diagnostics' && <SystemDiagnosticsTab />}

        {/* Legacy quick modal kept for active users only */}
        {showActiveUsersModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white dark:bg-secondary-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto border border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white flex items-center">
                  <Users className="h-5 w-5 mr-2 text-purple-500" />
                  Active Users Details
                </h2>
                <button
                  onClick={() => setShowActiveUsersModal(false)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {loadingActiveUsers ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="animate-spin h-8 w-8 text-primary-500" />
                  <span className="ml-2 text-secondary-500">Loading active users…</span>
                </div>
              ) : activeUsersDetails.length > 0 ? (
                <div className="space-y-3">
                  {activeUsersDetails.map((user, index) => (
                    <div
                      key={index}
                      className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm font-medium text-secondary-500">User</p>
                          <p className="text-sm font-medium">
                            {user.full_name || user.email}
                          </p>
                          {user.role && (
                            <p className="text-xs text-secondary-500 capitalize">{user.role}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-secondary-500">Email</p>
                          <p className="text-sm font-mono break-all">{user.email}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-secondary-500">Last Activity</p>
                          <p className="text-sm">
                            {user.last_activity
                              ? formatTimestampUTC5(user.last_activity)
                              : 'Unknown'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-secondary-500">Status</p>
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                            <span className="text-sm text-green-600 font-medium">Online</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-secondary-500">
                  <p>No active users found.</p>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;
